import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { t } from "@/i18n-js/instance";
import { FieldElement } from "@/react/components/FormRenderer/FormContent/ElementTypes/FieldElement";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement as FieldElementType } from "@/react/types/Element";
import { useBlocksContext } from "../../contexts/BlocksContext";

interface EmailBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
}
export interface FormContactEmailBlockProps
  extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: EmailBlockMeta;
  label?: string;
  placeholder?: string;
}

export const FormContactEmailBlock = forwardRef<
  HTMLDivElement,
  FormContactEmailBlockProps
>(
  (
    {
      label = t("settings.forms.default_contact_email_label"),
      placeholder = "",
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();

    const element: FieldElementType = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        label,
        placeholder,
        hidden: false,
        required: true,
        field_type: "contact_email",
      },
    };

    const shouldFocus =
      "isModal" in context &&
      "initialFocusRef" in context &&
      context.isModal &&
      blockInstanceMeta.fieldIndex === 0;

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <FieldElement
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? 0}
          errors={errors}
          initialFocusRef={
            shouldFocus && "initialFocusRef" in context
              ? context.initialFocusRef
              : undefined
          }
        />
      </div>
    );
  },
);

FormContactEmailBlock.displayName = "FormContactEmailBlock";
